<template>
  <div>
    <div v-if="availableFilters.length > 0" class="tw:mb-4">
      <Filters v-model="filters" :base-filters="baseFilters" :available-filters="availableFilters" />
    </div>

    <slot />
  </div>
</template>

<script setup lang="ts">
import { provide, reactive, ref, watch } from 'vue';
import qs from 'qs';
import Filters from '@/components/filters/Filters.vue';
import type { FilterDefinition } from '@/components/filters/filters';
import { METRIC_BOARD_KEY, type MetricBoardContext } from './metrics';

/**
 * Holds what every metric of a page is read with: the filters that scope the
 * page, which the reader cannot change, and the ones the reader picks. The
 * cards it holds read their own figure, each on its own.
 */
const props = withDefaults(defineProps<{
  availableFilters?: FilterDefinition[]
  baseFilters?: Record<string, any>
}>(), {
  availableFilters: () => [],
  baseFilters: () => ({}),
});

/**
 * Read from the address in setup rather than once mounted, so the cards are
 * created with the filters the page was opened with and read only once.
 *
 * A filter holds a single string, its values joined the way the filter writes
 * them: anything else the address carries, such as a hand-written
 * `filter[status][]=…`, is left for the filter default.
 */
function initialFilters(): Record<string, string> {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const fromUrl: Record<string, unknown> = typeof parsed.filter === 'object' && parsed.filter !== null && !Array.isArray(parsed.filter)
    ? parsed.filter
    : {};

  return Object.fromEntries(
    props.availableFilters
      .map((filter): [string, string | undefined] => {
        const value = fromUrl[filter.name];

        return [filter.name, typeof value === 'string' && value !== '' ? value : filter.default];
      })
      .filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '')
  );
}

const filters = ref<Record<string, string | boolean>>(initialFilters());

const context = reactive<MetricBoardContext>({
  filters: { ...props.baseFilters, ...filters.value },
});

provide(METRIC_BOARD_KEY, context);

/**
 * The address follows the filters, so a page can be reloaded or shared on
 * the reading it shows. Only the reader's own filters go in it: the page
 * scope is in the path already.
 */
function syncAddress(): void {
  const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const search = qs.stringify({ ...query, filter: filters.value }, { addQueryPrefix: true });

  window.history.replaceState(window.history.state, '', window.location.pathname + search);
}

watch(
  () => JSON.stringify([props.baseFilters, filters.value]),
  () => {
    context.filters = { ...props.baseFilters, ...filters.value };
    syncAddress();
  },
);
</script>
